import React, { useState, useEffect, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { useAuth } from './auth/AuthContext';
import '../css/meeting-selection.css';

export function MeetingSelection() {
    const { user, getToken, loading: authLoading, rolesLoading, isAdmin } = useAuth();
    const history = useHistory();
    const [meetings, setMeetings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [showNewForm, setShowNewForm] = useState(false);
    const [newName, setNewName] = useState('');
    const [newYear, setNewYear] = useState(new Date().getFullYear());
    const [newDate, setNewDate] = useState('');
    const [newBroeIdentifier, setNewBroeIdentifier] = useState('');
    const [creating, setCreating] = useState(false);
    const [createError, setCreateError] = useState('');
    const [createNotice, setCreateNotice] = useState('');

    const fetchMeetings = useCallback(async () => {
        try {
            const token = getToken();
            const response = await fetch('/api/meeting', {
                headers: token ? { Authorization: `Bearer ${token}` } : {}
            });

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}`);
            }

            const data = await response.json();
            const meetingsList = Array.isArray(data) ? data : [];

            if (meetingsList.length === 0) {
                setError('No meetings found. Create a new meeting to get started.');
                setMeetings([]);
            } else {
                setMeetings(meetingsList);
                setError('');
            }
        } catch (err) {
            console.error('Error fetching meetings:', err);
            setError('Failed to load meetings. Please try again.');
            setMeetings([]);
        } finally {
            setLoading(false);
        }
    }, [getToken]);

    useEffect(() => {
        if (!user || authLoading || rolesLoading) return;
        fetchMeetings();
    }, [user, authLoading, rolesLoading, fetchMeetings]);

    const handleCreateCompetition = async (e) => {
        e.preventDefault();
        setCreateError('');
        setCreateNotice('');

        const trimmedName = newName.trim();
        if (!trimmedName) {
            setCreateError('Competition name is required.');
            return;
        }

        const year = Number(newYear);
        if (!Number.isFinite(year) || year < 2000 || year > 2100) {
            setCreateError('Year must be between 2000 and 2100.');
            return;
        }

        setCreating(true);
        try {
            const token = getToken();
            const response = await fetch('/api/tenant/meeting', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    ...(token ? { Authorization: `Bearer ${token}` } : {})
                },
                body: JSON.stringify({
                    regattaName: trimmedName,
                    regattaYear: year,
                    meetingDate: newDate || null,
                    broeMeetingIdentifier: newBroeIdentifier.trim()
                })
            });

            const payload = await response.json().catch(() => ({}));
            if (!response.ok) {
                if (response.status === 402) {
                    setCreateError(payload?.message || 'No meeting credits available. Buy one-off access or upgrade to annual unlimited.');
                } else {
                    setCreateError(payload?.message || `Unable to create competition (HTTP ${response.status}).`);
                }
                return;
            }

            const createdName = payload?.regattaName || trimmedName;
            const broeIdentifier = newBroeIdentifier.trim();
            setCreateNotice(`Created "${createdName}".`);
            setNewName('');
            setNewDate('');
            setNewBroeIdentifier('');
            setShowNewForm(false);

            // If a BROE identifier was supplied, run an initial sync straight away.
            if (broeIdentifier && payload?.id) {
                setCreateNotice(`Created "${createdName}". Syncing entries from BROE...`);
                try {
                    const importResponse = await fetch('/api/crew/import', {
                        method: 'POST',
                        headers: {
                            'x-meeting-id': String(payload.id),
                            ...(token ? { Authorization: `Bearer ${token}` } : {})
                        }
                    });
                    const importPayload = await importResponse.json().catch(() => ({}));
                    if (importResponse.ok) {
                        const synced = Number(importPayload?.added || 0) + Number(importPayload?.updated || 0);
                        setCreateNotice(`Created "${createdName}" and synced ${synced} entr${synced === 1 ? 'y' : 'ies'} from BROE.`);
                    } else {
                        setCreateNotice(`Created "${createdName}", but the initial BROE sync failed: ${importPayload?.message || `HTTP ${importResponse.status}`}. You can run Import from BROE in Competition Admin.`);
                    }
                } catch (importErr) {
                    console.error('Initial BROE sync failed:', importErr);
                    setCreateNotice(`Created "${createdName}", but the initial BROE sync failed. You can run Import from BROE in Competition Admin.`);
                }
            }

            setLoading(true);
            await fetchMeetings();
        } catch (err) {
            console.error('Error creating competition:', err);
            setCreateError('Unable to create competition. Please try again.');
        } finally {
            setCreating(false);
        }
    };

    const handleSelectMeeting = useCallback((meeting) => {
        setSubmitting(true);
        // Route to a meeting-scoped public page so all role types can continue from here.
        if (meeting.urlSlug) {
            history.push(`/${meeting.urlSlug}/live-timetable`);
        } else {
            history.push(`/live-timetable?meetingId=${meeting.id}`);
        }
    }, [history]);

    if (authLoading || rolesLoading) {
        return (
            <div className="ms-container">
                <div className="ms-content">
                    <div className="ms-spinner">Loading...</div>
                </div>
            </div>
        );
    }

    if (!user) {
        return (
            <div className="ms-container">
                <div className="ms-content">
                    <div className="ms-error">Please log in to continue.</div>
                </div>
            </div>
        );
    }

    return (
        <div className="ms-container">
            <div className="ms-content">
                {error && (
                    <div className="ms-error-banner">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <circle cx="12" cy="12" r="10" />
                            <line x1="12" y1="8" x2="12" y2="12" />
                            <line x1="12" y1="16" x2="12.01" y2="16" />
                        </svg>
                        {error}
                    </div>
                )}

                {isAdmin && !loading && (
                    <div className="ms-new-competition">
                        {createNotice && !showNewForm && (
                            <div className="ms-create-notice">{createNotice}</div>
                        )}
                        {!showNewForm ? (
                            <button
                                type="button"
                                className="ms-btn ms-btn-select ms-btn-new"
                                onClick={() => { setShowNewForm(true); setCreateError(''); setCreateNotice(''); }}
                            >
                                + New Competition
                            </button>
                        ) : (
                            <form className="ms-new-form" onSubmit={handleCreateCompetition}>
                                <h3>New Competition</h3>
                                {createError && <div className="ms-error">{createError}</div>}
                                <div className="ms-new-form-grid">
                                    <label className="ms-field ms-field-wide">
                                        <span>Competition name</span>
                                        <input
                                            type="text"
                                            value={newName}
                                            onChange={(e) => setNewName(e.target.value)}
                                            placeholder="e.g. Kingston Regatta"
                                            maxLength={120}
                                            required
                                            autoFocus
                                        />
                                    </label>
                                    <label className="ms-field">
                                        <span>Year</span>
                                        <input
                                            type="number"
                                            min="2000"
                                            max="2100"
                                            value={newYear}
                                            onChange={(e) => setNewYear(e.target.value)}
                                            required
                                        />
                                    </label>
                                    <label className="ms-field">
                                        <span>Date</span>
                                        <input
                                            type="date"
                                            value={newDate}
                                            onChange={(e) => setNewDate(e.target.value)}
                                        />
                                    </label>
                                    <label className="ms-field ms-field-wide">
                                        <span>BROE meeting identifier (optional)</span>
                                        <input
                                            type="text"
                                            value={newBroeIdentifier}
                                            onChange={(e) => setNewBroeIdentifier(e.target.value)}
                                            placeholder="Can be added later in System Settings"
                                        />
                                    </label>
                                </div>
                                <div className="ms-new-form-actions">
                                    <button type="submit" className="ms-btn ms-btn-select" disabled={creating}>
                                        {creating ? 'Creating...' : 'Create Competition'}
                                    </button>
                                    <button
                                        type="button"
                                        className="ms-btn ms-btn-cancel"
                                        onClick={() => { setShowNewForm(false); setCreateError(''); }}
                                        disabled={creating}
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </form>
                        )}
                    </div>
                )}

                {loading ? (
                    <div className="ms-loading">
                        <div className="ms-spinner-dot" />
                        <p>Loading your meetings...</p>
                    </div>
                ) : meetings.length > 0 ? (
                    <div className="ms-list">
                        {meetings.map((meeting) => (
                            <div key={meeting.id} className="ms-list-item">
                                <div className="ms-list-info">
                                    <div className="ms-list-name">{meeting.regattaName || 'Unnamed Meeting'}</div>
                                    <div className="ms-list-meta">
                                        <span className="ms-list-date">
                                            {meeting.regattaDate ? new Date(meeting.regattaDate).toLocaleDateString() : 'No date'}
                                        </span>
                                        <span className="ms-list-separator">•</span>
                                        <span className="ms-list-crews">
                                            {meeting.totalCrews || 0} crew{meeting.totalCrews !== 1 ? 's' : ''}
                                        </span>
                                    </div>
                                </div>
                                <button
                                    className="ms-btn ms-btn-select"
                                    onClick={() => handleSelectMeeting(meeting)}
                                    disabled={submitting}
                                >
                                    {submitting ? 'Loading...' : 'Select'}
                                </button>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="ms-empty">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                            <line x1="16" y1="2" x2="16" y2="6" />
                            <line x1="8" y1="2" x2="8" y2="6" />
                            <line x1="3" y1="10" x2="21" y2="10" />
                        </svg>
                        <h3>No Meetings Available</h3>
                        <p>You don't have any meetings to manage yet.</p>
                    </div>
                )}
            </div>
        </div>
    );
}
